import { Button } from '@folio/components';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangleIcon } from 'lucide-react';
import {
  type ImpersonationSession,
  exitImpersonation,
  legacyPermissionsControllerAtMeQueryOptions,
  readImpersonationSession,
} from 'korint-sdk';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

const TICK_MS = 30_000;

const minutesLeft = (expiresAt: Date, now: Date): number =>
  Math.max(0, Math.ceil((expiresAt.getTime() - now.getTime()) / 60_000));

/**
 * Always-visible warning that the operator is acting as one of the tenant's own
 * users. Writes are live and attributed, so this is deliberately alarming rather
 * than a status chip, and it cannot be dismissed.
 *
 * Mounted inside the app shell, next to `EnvironmentBanner`, so the email lookup
 * below runs with a bearer already attached. Mounting it above the auth gate
 * instead makes that query fire before the interceptor exists, and its 401 reaches
 * KorintConfigProvider's global error toast — whose state update re-renders the
 * root and refetches, looping. The query options guard against that anyway.
 */
export function ImpersonationBanner() {
  const { t } = useTranslation();
  const [session, setSession] = useState<ImpersonationSession | null>(() =>
    readImpersonationSession(),
  );
  const [now, setNow] = useState(() => new Date());
  // The Cognito sub names nobody. `disableDefaultErrorHandling` + no retry so an
  // unauthorized render degrades to the sub instead of driving the global toast.
  const { data: target } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
    enabled: session !== null,
    retry: false,
    staleTime: 5 * 60 * 1000,
    meta: { disableDefaultErrorHandling: true },
  });

  useEffect(() => {
    if (!session) return;

    const timer = setInterval(() => {
      const current = new Date();
      setNow(current);
      // Expiry is enforced by the gateway; this just stops the banner claiming
      // a session that can no longer do anything.
      if (!readImpersonationSession(current)) {
        setSession(null);
      }
    }, TICK_MS);

    return () => clearInterval(timer);
  }, [session]);

  if (!session) return null;

  const remaining = minutesLeft(session.expiresAt, now);

  return (
    <Group
      role="alert"
      align="start"
      gap={3}
      className="sticky top-0 z-[300] w-full shrink-0 bg-red-700 px-4 py-2 text-white"
    >
      <AlertTriangleIcon className="mt-0.5 size-5 shrink-0" />
      <Stack gap={0} className="min-w-0 flex-1 gap-0.5">
        <Text size="sm" weight="bold">
          {t('impersonationBanner.label')} —{' '}
          {t('impersonationBanner.viewingAs', { user: target?.email ?? session.targetUserId })}
        </Text>
        <Text size="xs">
          {t('impersonationBanner.warning')}{' '}
          {t('impersonationBanner.actor', { actor: session.actor })}
          {' · '}
          {remaining > 0
            ? t('impersonationBanner.expiresIn', { minutes: remaining })
            : t('impersonationBanner.expired')}
        </Text>
      </Stack>
      <Button
        size="sm"
        variant="outline"
        className="shrink-0 border-white bg-transparent text-white hover:bg-white hover:text-red-700"
        onClick={exitImpersonation}
      >
        {t('impersonationBanner.exit')}
      </Button>
    </Group>
  );
}
